'use client';
import React, { useState } from 'react';
import { adminUsers } from '@/lib/mockData';
import type { Language } from '@/lib/i18n';
import { t } from '@/lib/i18n';
import { Search, UserX, UserCheck, Flame, Star } from 'lucide-react';
import { toast } from 'sonner';

interface Props { language: Language }

const RANK_COLORS: Record<string, string> = {
  'Expert Comptable': 'bg-purple-100 text-purple-700',
  'Comptable Senior': 'bg-blue-100 text-blue-700',
  'Comptable': 'bg-green-100 text-green-700',
  'Assistant Comptable': 'bg-amber-100 text-amber-700',
  'Apprenti': 'bg-orange-100 text-orange-700',
  'Étudiant': 'bg-gray-100 text-gray-600',
};

export default function AdminUsers({ language }: Props) {
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState(adminUsers);

  const filtered = users.filter(
    (u) =>
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase())
  );

  const toggleStatus = (userId: string) => {
    // Backend integration point: PATCH /api/users/:id/status
    setUsers((prev) =>
      prev.map((u) =>
        u.id === userId
          ? { ...u, status: u.status === 'active' ? 'suspended' : 'active' }
          : u
      )
    );
    toast.success(language === 'fr' ? 'Statut mis à jour' : 'Статус обновлен');
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t(language, 'common_search')}
            className="w-full pl-9 pr-4 py-2.5 bg-card border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>
        <span className="text-sm text-muted-foreground">
          {filtered.length} {language === 'fr' ? 'utilisateurs' : 'пользователей'}
        </span>
      </div>

      {/* Table */}
      <div className="bg-card rounded-2xl border border-border card-shadow overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-5 py-3 bg-muted/40 border-b border-border">
          {[
            { label: language === 'fr' ? 'Utilisateur' : 'Пользователь', span: 'col-span-3' },
            { label: language === 'fr' ? 'Email' : 'Email', span: 'col-span-3' },
            { label: language === 'fr' ? 'Rang' : 'Ранг', span: 'col-span-2' },
            { label: 'XP', span: 'col-span-1' },
            { label: '🔥', span: 'col-span-1' },
            { label: language === 'fr' ? 'Statut' : 'Статус', span: 'col-span-1' },
            { label: language === 'fr' ? 'Action' : 'Действие', span: 'col-span-1' },
          ].map((col) => (
            <div key={`user-col-${col.label}`} className={`${col.span} text-xs font-600 text-muted-foreground uppercase tracking-wide`}>
              {col.label}
            </div>
          ))}
        </div>

        <div className="divide-y divide-border">
          {filtered.map((user) => (
            <div
              key={`admin-user-${user.id}`}
              className="grid grid-cols-12 gap-2 px-5 py-3.5 items-center hover:bg-muted/30 transition-colors"
            >
              {/* User */}
              <div className="col-span-3 flex items-center gap-2.5 min-w-0">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-700 flex-shrink-0"
                  style={{ background: `hsl(${(user.name.charCodeAt(0) * 47) % 360}, 60%, 55%)` }}
                >
                  {user.name.split(' ').map((n) => n[0]).join('')}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-600 text-foreground truncate">{user.name}</p>
                  <p className="text-xs text-muted-foreground">{user.country === 'FR' ? '🇫🇷' : '🇷🇺'}</p>
                </div>
              </div>

              {/* Email */}
              <div className="col-span-3 min-w-0">
                <p className="text-xs text-muted-foreground truncate">{user.email}</p>
              </div>

              {/* Rank */}
              <div className="col-span-2">
                <span className={`text-xs font-600 px-2 py-0.5 rounded-full ${RANK_COLORS[user.rank] ?? 'bg-muted text-muted-foreground'}`}>
                  {user.rank}
                </span>
              </div>

              {/* XP */}
              <div className="col-span-1">
                <div className="flex items-center gap-0.5">
                  <Star size={10} className="text-primary" />
                  <span className="text-xs font-700 text-foreground font-tabular">
                    {(user.xp / 1000).toFixed(1)}k
                  </span>
                </div>
              </div>

              {/* Streak */}
              <div className="col-span-1">
                <div className="flex items-center gap-0.5">
                  <Flame size={10} className="text-accent" />
                  <span className="text-xs font-700 text-foreground font-tabular">{user.streak}</span>
                </div>
              </div>

              {/* Status */}
              <div className="col-span-1">
                <span
                  className={`text-xs font-600 px-2 py-0.5 rounded-full ${
                    user.status === 'active' ?'bg-green-100 text-green-700' :'bg-red-100 text-red-700'
                  }`}
                >
                  {user.status === 'active'
                    ? language === 'fr' ? 'Actif' : 'Активен'
                    : language === 'fr' ? 'Suspendu' : 'Заблокирован'}
                </span>
              </div>

              {/* Action */}
              <div className="col-span-1">
                <button
                  onClick={() => toggleStatus(user.id)}
                  className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
                    user.status === 'active' ?'bg-red-50 text-danger hover:bg-red-100' :'bg-green-50 text-success hover:bg-green-100'
                  }`}
                  title={user.status === 'active'
                    ? language === 'fr' ? 'Suspendre' : 'Заблокировать'
                    : language === 'fr' ? 'Activer' : 'Активировать'}
                >
                  {user.status === 'active' ? <UserX size={13} /> : <UserCheck size={13} />}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}